import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell, ScatterChart, Scatter, ZAxis, ReferenceArea, Line } from 'recharts';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui';

const formatCurrency = (value) => {
  const inThousands = value / 1000;
  return inThousands >= 1000
    ? `$${(inThousands / 1000).toFixed(3).replace(/\.?0+$/, '')}MM`
    : `$${inThousands.toLocaleString()}K`;
};

const STAGE_PERCENTAGES = {
  prospecting: 10,
  qualification: 25,
  processing: 50,
  awaitingApproval: 75,
  atDocs: 90
};

const PRODUCT_EFFORT_SCORE = {
  "30 Year Conv. Purch": 11,
  "30 Year Conv. Refi": 9,
  "30 Year Jumbo, Purch": 19,
  "30 Year Jumbo, Refi": 16,
  "15 YR Jumbo, Purch": 20,
  "15 YR Jumbo, Refi": 17,
  "15 Year Conv. Purch": 12,
  "15 Year Conv. Refi": 10,
  "5/1 ARM, Purch": 14,
  "5/1 ARM, Refi": 12,
  "7/1 ARM, Conv. Purch": 14,
  "7/1 ARM, Conv. Refi": 12,
  "10/1 ARM, Conv. Purch": 15,
  "10/1 ARM, Conv. Refi": 13,
  "Home Equity Line": 4,
  "Home Equity Loan": 6
};

const UNDERWRITERS = [
  'Carol Simmons',
  'Shannon O\'Brien',
  'Genice Hawkins',
  'Diane Fitzgerald',
  'Melissa Kowalski',
  'Patricia Larson',
  'Robert Chen'
];

const STAGES = [
  { id: "prospecting", name: "Prospecting", color: "hsl(var(--chart-2))", surface: "hsl(var(--chart-2) / 0.16)" },
  { id: "qualification", name: "Qualification", color: "hsl(var(--chart-3))", surface: "hsl(var(--chart-3) / 0.16)" },
  { id: "processing", name: "Processing", color: "hsl(var(--chart-1))", surface: "hsl(var(--chart-1) / 0.16)" },
  { id: "awaitingApproval", name: "Awaiting Approval", color: "hsl(var(--chart-4))", surface: "hsl(var(--chart-4) / 0.16)" },
  { id: "atDocs", name: "At Docs", color: "hsl(var(--chart-5))", surface: "hsl(var(--chart-5) / 0.16)" }
];

const MortgageLOS = () => {
  const [data, setData] = useState(null);
  const [selectedMLOTeam, setSelectedMLOTeam] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState("All Products");
  const [selectedMarket, setSelectedMarket] = useState('All Markets');
  const [selectedBranch, setSelectedBranch] = useState('All Branches');
  const [selectedLender, setSelectedLender] = useState('All Lenders');
  const [selectedUnderwriter, setSelectedUnderwriter] = useState('');
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [newlyDroppedItems, setNewlyDroppedItems] = useState(new Set());

  const calculateProbabilityAdjustedPipeline = useCallback(() => {
    if (!data) return 0;
    return data.opportunities.reduce((sum, opp) => {
      const stagePercentage = STAGE_PERCENTAGES[opp.stage] / 100;
      return sum + (opp.value * stagePercentage);
    }, 0) / 1000000; // Convert to millions
  }, [data]);

  const calculateTotalPipeline = useCallback(() => {
    if (!data) return 0;
    return data.opportunities.reduce((sum, opp) => sum + opp.value, 0) / 1000000; // Convert to millions
  }, [data]);

  const calculateUnderwriterLoad = useCallback(() => {
    if (!data) return [];
    const underwriterLoads = {};
    const underwriterCounts = {};
    data.opportunities.forEach(opp => {
      if (!underwriterLoads[opp.underwriter]) {
        underwriterLoads[opp.underwriter] = 0;
        underwriterCounts[opp.underwriter] = 0;
      }
      underwriterLoads[opp.underwriter] += PRODUCT_EFFORT_SCORE[opp.product] || 0;
      underwriterCounts[opp.underwriter]++;
    });
    return UNDERWRITERS
      .map(underwriter => ({
        name: underwriter,
        value: underwriterLoads[underwriter] || 0,
        count: underwriterCounts[underwriter] || 0
      }))
      .sort((a, b) => b.value - a.value); // Sort from high to low
  }, [data]);

  const filterOpportunities = useCallback((opportunities) => {
    if (!opportunities) return [];
    return opportunities.filter(opp => 
      (selectedMarket === 'All Markets' || opp.market === selectedMarket) &&
      (selectedBranch === 'All Branches' || opp.branch === selectedBranch) &&
      (selectedLender === 'All Lenders' || opp.lender === selectedLender) &&
      (selectedUnderwriter === '' || opp.underwriter === selectedUnderwriter) &&
      (selectedProduct === 'All Products' || opp.product === selectedProduct) &&
      (selectedMLOTeam === 'All' || opp.officer === selectedMLOTeam)
    );
  }, [selectedMarket, selectedBranch, selectedLender, selectedUnderwriter, selectedProduct, selectedMLOTeam]);

  const getOpportunitiesByStage = useCallback((stageId) => {
    if (!data) return [];
    return filterOpportunities(data.opportunities).filter(opp => opp.stage === stageId);
  }, [data, filterOpportunities]);

  const prepareStripPlotData = useCallback(() => {
    if (!data) return [];
    return filterOpportunities(data.opportunities).map(opp => {
      const stage = data.stages.find(s => s.id === opp.stage);
      return {
        x: stage.name,
        y: opp.daysInStage,
        z: opp.value,
        name: opp.name,
        id: opp.id,
        stage: stage.name,
        color: stage.color
      };
    });
  }, [data, filterOpportunities]);

  const calculateStageValue = useCallback((stageId) => {
    return getOpportunitiesByStage(stageId).reduce((sum, opp) => sum + opp.value, 0) / 1000000;
  }, [getOpportunitiesByStage]);

  useEffect(() => {
    // Simulating data fetch
    setData({
      summary: [
        { title: "QTD Closed", value: 17.7, change: 22, changeDirection: "up" },
        { title: "Total Pipeline", value: 0, change: 14, changeDirection: "up" }, // We'll update this
        { title: "Prob. Adj. Pipeline", value: 10.83, change: 33, changeDirection: "up" },
        { title: "Closing Next 10 Days", value: 3.44, change: 14, changeDirection: "up" },
        { title: "Close Date At Risk", value: 1.2, change: 14, changeDirection: "down" }
      ],
      stages: STAGES,
      opportunities: [
        { id: "1", name: "Miller Family", officer: "Sydney Cash", product: "30 Year Conv. Purch", expectedClose: "03/28/2025", value: 475000, stage: "prospecting", underwriter: "Carol Simmons", daysInStage: 3, market: "Miami, FL", branch: "Downtown Miami", lender: "First National Bank" },
        { id: "2", name: "Sarah Roberts", officer: "Pierre Gasly", product: "Home Equity Line", expectedClose: "02/17/2025", value: 40000, stage: "prospecting", underwriter: "Shannon O'Brien", daysInStage: 1, market: "Miami, FL", branch: "Brickell Financial District", lender: "Coastal Lending Group" },
        { id: "3", name: "William Becker", officer: "Esteban Ocan", product: "15 YR Jumbo, Refi", expectedClose: "02/14/2025", value: 985000, stage: "prospecting", underwriter: "Genice Hawkins", daysInStage: 5, market: "Atlanta, GA", branch: "Midtown Atlanta", lender: "Midwest Mortgage Co." },
        { id: "4", name: "Heath Family", officer: "Logan Sargeant", product: "30 Year Conv. Purch", expectedClose: "03/22/2025", value: 504000, stage: "qualification", underwriter: "Diane Fitzgerald", daysInStage: 7, market: "Atlanta, GA", branch: "Buckhead", lender: "Urban Home Loans" },
        { id: "5", name: "James Perry", officer: "Pierre Gasly", product: "Home Equity Line", expectedClose: "02/17/2025", value: 40000, stage: "qualification", underwriter: "Melissa Kowalski", daysInStage: 2, market: "Tampa, FL", branch: "South Tampa", lender: "Heartland Credit Union" },
        { id: "6", name: "Latesha Gray", officer: "Max Verstappen", product: "30 Year Conv. Purch", expectedClose: "02/14/2025", value: 475000, stage: "qualification", underwriter: "Patricia Larson", daysInStage: 4, market: "Orlando, FL", branch: "Winter Park", lender: "Sunrise Mortgage Services" },
        { id: "7", name: "Jill Oates", officer: "Sydney Cash", product: "30 Year Conv. Purch", expectedClose: "02/28/2025", value: 387000, stage: "processing", underwriter: "Robert Chen", daysInStage: 9, market: "Charlotte, NC", branch: "Uptown Charlotte", lender: "Liberty Financial" },
        { id: "8", name: "Samual Barney", officer: "Logan Sargeant", product: "Home Equity Loan", expectedClose: "02/17/2025", value: 40000, stage: "processing", underwriter: "Carol Simmons", daysInStage: 27, market: "Charlotte, NC", branch: "SouthPark", lender: "Evergreen Home Lending" },
        { id: "9", name: "William Becker", officer: "George Russell", product: "15 YR Jumbo, Refi", expectedClose: "02/17/2025", value: 1225000, stage: "processing", underwriter: "Shannon O'Brien", daysInStage: 12, market: "Nashville, TN", branch: "Downtown Nashville", lender: "Golden State Mortgages" },
        { id: "10", name: "Sarah Saban", officer: "Sergio Perez", product: "5/1 ARM, Refi", expectedClose: "02/28/2025", value: 722000, stage: "awaitingApproval", underwriter: "Genice Hawkins", daysInStage: 8, market: "Nashville, TN", branch: "Green Hills", lender: "First National Bank" },
        { id: "11", name: "Greg Malkovic", officer: "Pierre Gasly", product: "Home Equity Loan", expectedClose: "02/01/2025", value: 80000, stage: "awaitingApproval", underwriter: "Diane Fitzgerald", daysInStage: 10, market: "Jacksonville, FL", branch: "Jacksonville Beach", lender: "Coastal Lending Group" },
        { id: "12", name: "Esteban Hamilton", officer: "Esteban Ocan", product: "30 Year Conv. Purch", expectedClose: "01/22/2025", value: 455000, stage: "awaitingApproval", underwriter: "Melissa Kowalski", daysInStage: 15, market: "Jacksonville, FL", branch: "San Marco", lender: "Midwest Mortgage Co." },
        { id: "13", name: "Oscar Norris", officer: "Alex Albon", product: "7/1 ARM, Conv. Purch", expectedClose: "03/01/2025", value: 375000, stage: "atDocs", underwriter: "Patricia Larson", daysInStage: 4, market: "New Orleans, LA", branch: "French Quarter", lender: "Mountain State Finance" },
        { id: "14", name: "Lando Ricardo", officer: "Pierre Gasly", product: "Home Equity Line", expectedClose: "02/05/2025", value: 40000, stage: "atDocs", underwriter: "Robert Chen", daysInStage: 11, market: "New Orleans, LA", branch: "Metairie", lender: "Urban Home Loans" },
        { id: "15", name: "Charles Sianz", officer: "George Russell", product: "15 YR Jumbo, Refi", expectedClose: "01/19/2025", value: 955000, stage: "atDocs", underwriter: "Carol Simmons", daysInStage: 18, market: "Raleigh, NC", branch: "North Hills Raleigh", lender: "Heartland Credit Union" },
        { id: "16", name: "Emma Thompson", officer: "Sydney Cash", product: "30 Year Conv. Purch", expectedClose: "04/15/2025", value: 520000, stage: "prospecting", underwriter: "Shannon O'Brien", daysInStage: 2, market: "Raleigh, NC", branch: "Cary", lender: "Sunrise Mortgage Services" },
        { id: "17", name: "Michael Chen", officer: "Pierre Gasly", product: "15 Year Conv. Purch", expectedClose: "04/22/2025", value: 350000, stage: "prospecting", underwriter: "Genice Hawkins", daysInStage: 1, market: "Memphis, TN", branch: "East Memphis", lender: "Liberty Financial" },
        { id: "18", name: "Olivia Martinez", officer: "Esteban Ocan", product: "Home Equity Line", expectedClose: "04/30/2025", value: 75000, stage: "prospecting", underwriter: "Diane Fitzgerald", daysInStage: 10, market: "Memphis, TN", branch: "Germantown", lender: "Evergreen Home Lending" },
        { id: "19", name: "Daniel Lee", officer: "Logan Sargeant", product: "5/1 ARM, Purch", expectedClose: "05/05/2025", value: 680000, stage: "prospecting", underwriter: "Melissa Kowalski", daysInStage: 15, market: "Miami, FL", branch: "Downtown Miami", lender: "Golden State Mortgages" },
        { id: "20", name: "Sophia Wilson", officer: "Max Verstappen", product: "30 Year Jumbo, Purch", expectedClose: "05/12/2025", value: 1100000, stage: "prospecting", underwriter: "Patricia Larson", daysInStage: 4, market: "Miami, FL", branch: "Brickell Financial District", lender: "First National Bank" },
        { id: "21", name: "Ethan Brown", officer: "George Russell", product: "15 YR Jumbo, Purch", expectedClose: "05/18/2025", value: 950000, stage: "qualification", underwriter: "Robert Chen", daysInStage: 24, market: "Atlanta, GA", branch: "Midtown Atlanta", lender: "Coastal Lending Group" },
        { id: "22", name: "Ava Johnson", officer: "Sergio Perez", product: "Home Equity Loan", expectedClose: "05/25/2025", value: 120000, stage: "qualification", underwriter: "Carol Simmons", daysInStage: 19, market: "Atlanta, GA", branch: "Buckhead", lender: "Midwest Mortgage Co." },
        { id: "23", name: "Noah Garcia", officer: "Alex Albon", product: "10/1 ARM, Conv. Purch", expectedClose: "06/01/2025", value: 725000, stage: "qualification", underwriter: "Shannon O'Brien", daysInStage: 4, market: "Tampa, FL", branch: "South Tampa", lender: "Mountain State Finance" },
        { id: "24", name: "Isabella Taylor", officer: "Sydney Cash", product: "30 Year Conv. Refi", expectedClose: "06/08/2025", value: 410000, stage: "processing", underwriter: "Genice Hawkins", daysInStage: 12, market: "Orlando, FL", branch: "Winter Park", lender: "Urban Home Loans" },
        { id: "25", name: "Liam Anderson", officer: "Pierre Gasly", product: "7/1 ARM, Conv. Refi", expectedClose: "06/15/2025", value: 550000, stage: "awaitingApproval", underwriter: "Diane Fitzgerald", daysInStage: 9, market: "Charlotte, NC", branch: "Uptown Charlotte", lender: "Heartland Credit Union" }
      ]
    });
  }, []);

  useEffect(() => {
    if (data) {
      // Update the "Total Pipeline" and "Probability Adj Pipeline" summary items
      setData(prevData => {
        const updatedSummary = prevData.summary.map(item => {
          if (item.title === "Total Pipeline") {
            return { ...item, value: calculateTotalPipeline().toFixed(2) };
          } else if (item.title === "Prob. Adj. Pipeline") {
            return { ...item, value: calculateProbabilityAdjustedPipeline().toFixed(2) };
          }
          return item;
        });
        return { ...prevData, summary: updatedSummary };
      });
    }
  }, [data, calculateProbabilityAdjustedPipeline, calculateTotalPipeline]);

  const onDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", id);
    setDraggedItemId(id);
  };

  const onDragEnd = () => {
    setDraggedItemId(null);
    setDragOverStage(null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDragEnter = (e, stageId) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOverStage(null);
  };

  const onDrop = (e, targetStage) => {
    e.preventDefault();
    const opportunityId = e.dataTransfer.getData("text");
    setDraggedItemId(null);
    setDragOverStage(null);
    
    // Add item to newly dropped set for animation
    setNewlyDroppedItems(prev => new Set([...prev, opportunityId]));
    
    // Remove animation class after animation completes
    setTimeout(() => {
      setNewlyDroppedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(opportunityId);
        return newSet;
      });
    }, 500); // Match rollDown animation duration
    
    setData(prevData => {
      const updatedOpportunities = prevData.opportunities.map(opp => 
        opp.id === opportunityId ? { ...opp, stage: targetStage } : opp
      );
      const updatedSummary = prevData.summary.map(item => {
        if (item.title === "Total Pipeline") {
          return { ...item, value: calculateTotalPipeline().toFixed(2) };
        } else if (item.title === "Prob. Adj. Pipeline") {
          return { ...item, value: calculateProbabilityAdjustedPipeline().toFixed(2) };
        }
        return item;
      });
      return { ...prevData, opportunities: updatedOpportunities, summary: updatedSummary };
    });
  };

  if (!data) return <div className="text-foreground">Loading...</div>;

  const getColorForValue = (value) => {
    if (value > 45) return 'hsl(var(--chart-down))';
    if (value > 35) return 'hsl(var(--destructive))';
    if (value > 30) return 'hsl(var(--primary))';
    if (value > 20) return 'hsl(var(--chart-2))';
    if (value > 10) return 'hsl(var(--chart-3))';
    if (value > 5) return 'hsl(var(--accent))';
    return 'hsl(var(--secondary))';
  };

  const axisTickStyle = { fill: 'hsl(var(--chart-axis))', fontSize: 12 };
  const legendFormatter = (value) => <span className="ace-chart-legend-text">{value}</span>;

  const mloTeamNames = [
    'All',
    'Sydney Cash',
    'Pierre Gasly',
    'Esteban Ocan',
    'Logan Sargeant',
    'Max Verstappen',
    'George Russell',
    'Sergio Perez',
    'Alex Albon'
  ];

  const productHierarchy = {
    Mortgage: [
      "30 Year Conv. Purch",
      "30 Year Conv. Refi",
      "30 Year Jumbo, Purch",
      "30 Year Jumbo, Refi",
      "15 YR Jumbo, Purch",
      "15 YR Jumbo, Refi",
      "15 Year Conv. Purch",
      "15 Year Conv. Refi",
      "5/1 ARM, Purch",
      "5/1 ARM, Refi",
      "7/1 ARM, Conv. Purch",
      "7/1 ARM, Conv. Refi",
      "10/1 ARM, Conv. Purch",
      "10/1 ARM, Conv. Refi",
    ],
    "Home Equity": [
      "Home Equity Line",
      "Home Equity Loan"
    ]
  };

  const lenders = [
    'All Lenders',
    'First National Bank',
    'Midwest Mortgage Co.',
    'Coastal Lending Group',
    'Mountain State Finance',
    'Urban Home Loans',
    'Heartland Credit Union',
    'Sunrise Mortgage Services',
    'Liberty Financial',
    'Evergreen Home Lending',
    'Golden State Mortgages'
  ];

  const markets = [
    'All Markets',
    'Miami, FL',
    'Atlanta, GA',
    'Tampa, FL',
    'Orlando, FL',
    'Charlotte, NC',
    'Nashville, TN',
    'Jacksonville, FL',
    'New Orleans, LA',
    'Raleigh, NC',
    'Memphis, TN'
  ];

  const branches = [
    'All Branches',
    'Downtown Miami',
    'Brickell Financial District',
    'Midtown Atlanta',
    'Buckhead',
    'South Tampa',
    'Winter Park',
    'Uptown Charlotte',
    'SouthPark',
    'Downtown Nashville',
    'Green Hills',
    'Jacksonville Beach',
    'San Marco',
    'French Quarter',
    'Metairie',
    'North Hills Raleigh',
    'Cary',
    'East Memphis',
    'Germantown'
  ];

  return (
    <div className="space-y-5">
      <section className="ace-rm-overview">
        <span className="ace-rm-eyebrow">OP_MLOS · mortgage-los</span>
        <h1 className="ace-rm-title">
          Mortgage pipeline execution
          <span className="ace-serif text-primary"> with clearer operational signal.</span>
        </h1>
        <p className="ace-rm-description">
          Drag-and-drop workflow management, underwriting pressure tracking, and stage-adjusted
          pipeline analytics all aligned to Ace hub light/dark styling standards.
        </p>
      </section>

      <div className="ace-scroll-shell overflow-x-auto pb-2">
        <div className="grid min-w-[1120px] grid-cols-5 gap-4">
          {data.summary.map((item, index) => (
            <div key={index} className="card-wrapper p-4">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {item.title}
              </h2>
              <div className="text-2xl font-bold text-foreground">${item.value}MM</div>
              <div className={`mt-1 text-sm ${item.changeDirection === 'up' ? 'text-accent' : 'text-destructive'}`}>
                {item.changeDirection === 'up' ? '↑' : '↓'}{item.change}% vs [PQ]
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-wrapper p-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6">
          <Select value={selectedMarket} onValueChange={setSelectedMarket}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Market: All" />
            </SelectTrigger>
            <SelectContent>
              {markets.map((market) => (
                <SelectItem key={market} value={market}>
                  {market === 'All Markets' ? 'Market: All' : market}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Branch: All" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((branch) => (
                <SelectItem key={branch} value={branch}>
                  {branch === 'All Branches' ? 'Branch: All' : branch}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedLender} onValueChange={setSelectedLender}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Lender: All" />
            </SelectTrigger>
            <SelectContent>
              {lenders.map((lender) => (
                <SelectItem key={lender} value={lender}>
                  {lender === 'All Lenders' ? 'Lender: All' : lender}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedUnderwriter || "All"} onValueChange={(value) => setSelectedUnderwriter(value === "All" ? "" : value)}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Underwriter: All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">Underwriter: All</SelectItem>
              {UNDERWRITERS.map((underwriter) => (
                <SelectItem key={underwriter} value={underwriter}>
                  {underwriter}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedProduct} onValueChange={setSelectedProduct}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Product: All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All Products">Product: All</SelectItem>
              {Object.entries(productHierarchy).map(([, products]) =>
                products.map((product) => (
                  <SelectItem key={product} value={product}>
                    {product}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Select value={selectedMLOTeam} onValueChange={setSelectedMLOTeam}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="MLO Team: All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">MLO Team: All</SelectItem>
              {mloTeamNames.filter(name => name !== 'All').map((name) => (
                <SelectItem key={name} value={name}>
                  MLO Team: {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="ace-scroll-shell overflow-x-auto px-1 pb-2">
        <div className="grid min-w-[1080px] grid-cols-5 gap-2.5">
          {data.stages.map(stage => (
            <div
              key={stage.id}
              onDragOver={onDragOver}
              onDragEnter={(e) => onDragEnter(e, stage.id)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, stage.id)}
              className={`card-wrapper px-2 py-2 transition-all ${dragOverStage === stage.id ? 'los-drop-zone' : ''}`}
            >
              <h3 className="mb-2 text-base font-semibold tracking-tight" style={{ color: stage.color }}>
                {stage.name} ({getOpportunitiesByStage(stage.id).length})
              </h3>
              <div className="mb-4 text-xl font-bold" style={{ color: stage.color }}>
                ${calculateStageValue(stage.id).toFixed(2)}MM
              </div>
              <div className="ace-scroll-shell los-stage-scroll max-h-[30rem] space-y-2 overflow-y-auto pr-0">
                {getOpportunitiesByStage(stage.id).map((opp) => (
                  <div
                    key={opp.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, opp.id)}
                    onDragEnd={onDragEnd}
                    className={`relative cursor-move rounded-xl border p-2 transition-all ${draggedItemId === opp.id ? 'los-dragging' : ''} ${newlyDroppedItems.has(opp.id) ? 'los-card-drop-in' : ''}`}
                    style={{ backgroundColor: stage.surface, borderColor: stage.color }}
                  >
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <h4 className="text-base font-semibold text-foreground">{opp.name}</h4>
                      <div
                        className="rounded-md px-2 py-0.5 text-xs font-semibold"
                        style={{ backgroundColor: stage.color, color: 'hsl(var(--background))' }}
                      >
                        {formatCurrency(opp.value)}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Officer:</span> {opp.officer}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Product:</span> {opp.product}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Expected Close:</span> {opp.expectedClose}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="card-wrapper p-3 lg:col-span-3">
          <h2 className="mb-2 text-lg font-semibold text-primary">Underwriting Load</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={calculateUnderwriterLoad()} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
              <XAxis type="number" stroke="hsl(var(--chart-axis))" tick={axisTickStyle} />
              <YAxis dataKey="name" type="category" stroke="hsl(var(--chart-axis))" tick={axisTickStyle} width={110} />
              <Tooltip
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '10px' }}
                labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 'bold' }}
                itemStyle={{ color: 'hsl(var(--muted-foreground))' }}
                formatter={(value, name, props) => {
                  const count = props.payload.count;
                  return [`${value} (${count} opportunities)`, 'Workload'];
                }}
              />
              <Bar dataKey="value" fill="hsl(var(--secondary))">
                {calculateUnderwriterLoad().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColorForValue(entry.value)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-wrapper p-3 lg:col-span-4">
          <h2 className="mb-2 text-lg font-semibold text-primary">Opportunities by Days in Stage</h2>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart
              margin={{
                top: 10,
                right: 10,
                bottom: 40,
                left: 0,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
              <XAxis
                dataKey="x"
                type="category"
                allowDuplicatedCategory={false}
                stroke="hsl(var(--chart-axis))"
                angle={-45}
                textAnchor="end"
                interval={0}
                tick={axisTickStyle}
                height={70}
              />
              <YAxis
                dataKey="y"
                name="Days in Stage"
                stroke="hsl(var(--chart-axis))"
                tick={axisTickStyle}
                domain={[0, 30]}
                ticks={[0, 10, 20, 30]}
                width={34}
              />
              <ZAxis dataKey="z" range={[40, 120]} name="Value" />
              <Tooltip
                cursor={{ strokeDasharray: '3 3', stroke: 'hsl(var(--chart-axis))' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const point = payload[0].payload;
                    return (
                      <div className="ace-tooltip p-4">
                        <p className="font-semibold" style={{ color: point.color }}>{point.name}</p>
                        <p className="text-sm text-muted-foreground">{`Stage: ${point.stage}`}</p>
                        <p className="text-sm text-muted-foreground">{`Days in Stage: ${point.y}`}</p>
                        <p className="text-sm text-muted-foreground">{`Value: ${formatCurrency(point.z)}`}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <ReferenceArea y1={20} y2={30} fill="hsl(var(--primary) / 0.14)" />
              <Scatter data={prepareStripPlotData()}>
                {prepareStripPlotData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="card-wrapper p-3 lg:col-span-5">
          <h2 className="mb-2 text-lg font-semibold text-primary">Volume Trend by Stage</h2>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              data={[
                { name: 'May', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 0.9])) },
                { name: 'Jun', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 1.0])) },
                { name: 'Jul', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 1.1])) },
                { name: 'Aug', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 1.2])) },
                { name: 'Sep', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 1.1])) },
                { name: 'Oct', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 1.0])) },
                { name: 'Nov', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 0.9])) },
                { name: 'Dec', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 0.8])) },
                { name: 'Jan', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 0.7])) },
                { name: 'Feb', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 0.8])) },
                { name: 'Mar', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 0.9])) },
                { name: 'Apr', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 1.0])) },
                { name: 'May', ...Object.fromEntries(data.stages.map(stage => [stage.name, calculateStageValue(stage.id) * 1.1])) },
              ].map(month => ({
                ...month,
                probAdjusted: Object.entries(month)
                  .filter(([key]) => key !== 'name')
                  .reduce((sum, [stage, value]) => sum + value * (STAGE_PERCENTAGES[data.stages.find(s => s.name === stage).id] / 100), 0)
              }))
            }
              margin={{
                top: 10,
                right: 10,
                bottom: 20,
                left: 0,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
              <XAxis dataKey="name" stroke="hsl(var(--chart-axis))" tick={axisTickStyle} />
              <YAxis stroke="hsl(var(--chart-axis))" tick={axisTickStyle} width={34} />
              <Tooltip
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '10px' }}
                labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 'bold' }}
                itemStyle={{ color: 'hsl(var(--muted-foreground))' }}
                formatter={(value, name) => {
                  if (name === 'probAdjusted') {
                    return [value.toFixed(2), 'Prob. Adjusted'];
                  }
                  return [value.toFixed(2), name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: '10px' }} formatter={legendFormatter} />
              {data.stages.map(stage => (
                <Bar key={stage.id} dataKey={stage.name} stackId="a" fill={stage.color} />
              ))}
              <Line type="monotone" dataKey="probAdjusted" stroke="hsl(var(--chart-axis))" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MortgageLOS;